import { Card, CardContent, CardActions } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ButtonLink } from '@/components/ButtonLink'
import { assertUserIsAdminOrRedirect } from '@/features/admin/helper/assertUserIsAdminOrRedirect'
import { UserDetailsPendingComponent } from '@/features/admin/users/detail/components/UserDetailsPendingComponent'
import { UserDetailsTableLayout } from '@/features/admin/users/detail/components/UserDetailsTableLayout'
import { useUserDetailsProjectColumns } from '@/features/admin/users/detail/helper/userDetailsProjectColumns'
import { useUserDetailsWorkingTimeModelColumns } from '@/features/admin/users/detail/helper/userDetailsWorkingTimeModelColumns'
import { DeletedItemSelection } from '@/generated/Enumerations/DeletedItemSelection_pb'
import { stringifyId } from '@/helper/id'
import { userDetailsOptions, useUserDetails } from '@/queries/useUserDetails'
import {
    defaultPageSize,
    availablePageSizes,
} from '@/schemas/searchParams/searchParamsPaginationSchema'

const seleniumPrefix = 'view-users'

const RouteComponent: React.FC = () => {
    const { id } = Route.useParams()
    const { t } = useTranslation()

    const {
        data: { projects, user, userWorkingTimeModels },
    } = useUserDetails(id, DeletedItemSelection.BOTH)

    const userDetailsProjectColumns = useUserDetailsProjectColumns()
    const userDetailsWorkingTimeModelColumns =
        useUserDetailsWorkingTimeModelColumns()

    return (
        <Card>
            <CardContent>
                <UserDetailsTableLayout
                    firstName={user?.firstName}
                    lastName={user?.lastName}
                    email={user?.email}
                    emailConfirmed={
                        user?.emailConfirmed ? t('common.yes') : t('common.no')
                    }
                    roles={user?.roles.map(r => r.name)}
                />

                <DataGrid
                    sx={{ mt: 2 }}
                    rows={projects}
                    columns={userDetailsProjectColumns}
                    initialState={{
                        pagination: {
                            paginationModel: { pageSize: defaultPageSize },
                        },
                    }}
                    pageSizeOptions={availablePageSizes}
                    disableRowSelectionOnClick={true}
                    getRowId={row => stringifyId(row.id)}
                />

                <DataGrid
                    sx={{ mt: 2 }}
                    rows={userWorkingTimeModels}
                    columns={userDetailsWorkingTimeModelColumns}
                    initialState={{
                        pagination: {
                            paginationModel: { pageSize: defaultPageSize },
                        },
                    }}
                    pageSizeOptions={availablePageSizes}
                    disableRowSelectionOnClick={true}
                    getRowId={row => stringifyId(row.id)}
                />
            </CardContent>

            <CardActions style={{ justifyContent: 'flex-end' }}>
                <ButtonLink
                    to={'/admin/users'}
                    data-selenium={`${seleniumPrefix}-back`}
                >
                    {t('common.back')}
                </ButtonLink>
                <ButtonLink
                    to={'/admin/users/$id/edit'}
                    params={{ id }}
                    variant={'outlined'}
                    data-selenium={`${seleniumPrefix}-edit`}
                >
                    {t('common.edit')}
                </ButtonLink>
            </CardActions>
        </Card>
    )
}

export const Route = createFileRoute('/__base-layout/admin/users/$id/view')({
    component: RouteComponent,
    beforeLoad: async ({
        context: { user, grpcClients, queryClient },
        params: { id },
    }) => {
        assertUserIsAdminOrRedirect(user)

        await queryClient.ensureQueryData(
            userDetailsOptions(
                grpcClients,
                id,
                DeletedItemSelection.NORMAL_ONLY
            )
        )
    },
    pendingComponent: UserDetailsPendingComponent,
})
